import React, { useState } from 'react';
import { Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import PropTypes from 'prop-types';

const RegisterForm = ({ hasLabel = true }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    password: '',
    confirmPassword: '',
    isAccepted: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { updateProfile } = useAuth();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Limpiar error cuando el usuario empiece a escribir
    if (error) setError('');
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return false;
    }
    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return false;
    }
    if (!formData.isAccepted) {
      setError('Debes aceptar los términos y condiciones');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await authService.register({
        username: formData.username,
        email: formData.email,
        fullName: formData.fullName,
        password: formData.password
      });
      
      if (result.success) {
        if (result.autoLoginFailed) {
          // Registro exitoso pero login automático falló
          toast.warning(result.message, { theme: 'colored' });
          navigate('/login');
        } else {
          // Registro exitoso con login automático
          toast.success(`¡Bienvenido ${formData.fullName}! Tu cuenta ha sido creada exitosamente.`, {
            theme: 'colored'
          });
          
          // Actualizar el contexto de autenticación
          await updateProfile();
          
          // Redirigir al dashboard
          navigate('/');
        }
      } else {
        setError(result.error || 'Error al registrar usuario');
      }
    } catch (error) {
      setError('Error de conexión. Por favor, intenta nuevamente.');
      toast.error('Error de conexión. Verifique su conexión a internet.', {
        theme: 'colored'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Nombre Completo</Form.Label>}
        <Form.Control
          type="text"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder={!hasLabel ? 'Nombre Completo' : ''}
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Usuario</Form.Label>}
        <Form.Control
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          placeholder={!hasLabel ? 'Usuario' : ''}
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Correo Electrónico</Form.Label>}
        <Form.Control
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={!hasLabel ? 'Correo Electrónico' : ''}
          required
          disabled={isSubmitting}
        />
      </Form.Group>

      <Row className="g-2 mb-3">
        <Form.Group as={Col} sm={6}>
          {hasLabel && <Form.Label>Contraseña</Form.Label>}
          <div className="position-relative">
            <Form.Control
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={!hasLabel ? 'Contraseña' : ''}
              required
              disabled={isSubmitting}
            />
            <Button
              variant="link"
              className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent p-2"
              style={{ zIndex: 10 }}
              onClick={togglePasswordVisibility}
              disabled={isSubmitting}
              type="button"
            >
              <FontAwesomeIcon 
                icon={showPassword ? faEyeSlash : faEye} 
                className="text-700"
              />
            </Button>
          </div>
        </Form.Group>

        <Form.Group as={Col} sm={6}>
          {hasLabel && <Form.Label>Confirmar Contraseña</Form.Label>}
          <div className="position-relative">
            <Form.Control
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={!hasLabel ? 'Confirmar Contraseña' : ''}
              required
              disabled={isSubmitting}
            />
            <Button
              variant="link"
              className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent p-2"
              style={{ zIndex: 10 }}
              onClick={toggleConfirmPasswordVisibility}
              disabled={isSubmitting}
              type="button"
            >
              <FontAwesomeIcon 
                icon={showConfirmPassword ? faEyeSlash : faEye} 
                className="text-700"
              />
            </Button>
          </div>
        </Form.Group>
      </Row>

      <Form.Group className="mb-3">
        <Form.Check type="checkbox" id="acceptCheckbox" className="form-check">
          <Form.Check.Input
            type="checkbox"
            name="isAccepted"
            checked={formData.isAccepted}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          <Form.Check.Label className="form-label">
            Acepto los <Link to="#!">términos</Link> y{' '}
            <Link to="#!">política de privacidad</Link>
          </Form.Check.Label>
        </Form.Check>
      </Form.Group>

      <Form.Group>
        <Button
          type="submit"
          className="w-100 btn-hover-shadow"
          disabled={
            isSubmitting ||
            !formData.fullName ||
            !formData.username ||
            !formData.email ||
            !formData.password ||
            !formData.confirmPassword ||
            !formData.isAccepted
          }
        >
          {isSubmitting ? (
            <>
              <span 
                className="spinner-border spinner-border-sm me-2" 
                role="status" 
                aria-hidden="true"
              />
              Registrando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUserPlus} className="me-2" />
              Registrarse
            </>
          )}
        </Button>
      </Form.Group>
    </Form>
  );
};

RegisterForm.propTypes = {
  hasLabel: PropTypes.bool
};

export default RegisterForm; 